import { batch50Profiles, nextBatch50Profiles } from './castBatch50.ts'
import { researchedProfilesB } from './castBios2021B.ts'
import { researchedProfilesA } from './castBios2021A.ts'
import { researchedProfilesC } from './castBios2021C.ts'
import { legacyProfilesB } from './castBiosLegacyB.ts'
import { legacyProfilesA } from './castBiosLegacyA.ts'
import { legacyProfilesC } from './castBiosLegacyC.ts'
import { researchedProfilesCFollowup } from './castBios2021CFollowup.ts'
import { finalCarmellaProfiles } from './castBiosFinalCarmella.ts'
import { finalIdentityProfiles } from './castBiosFinalIdentities.ts'
import { finalHuntProfilesCE } from './castBiosFinalHuntCE.ts'
import { finalHuntProfilesCT } from './castBiosFinalHuntCT.ts'
import { familyChantelCanonProfiles } from './castBiosFamilyChantelCanon.ts'
import { darceyStaceyCanonProfiles } from './castBiosDarceyStaceyCanon.ts'
import { refreshExistingCanonProfiles } from './canonExistingProfileUpdates.ts'

export type CastProfileJourney = {
  label: string
  partnerName?: string
  relationshipId?: string
  era: string
  summary: string
}

export type CastProfileSource = {
  label: string
  url: string
}

export type CastProfile = {
  personId: string
  // A documented storyline is not a verified biography of its account operator.
  entryKind?: 'person' | 'story_identity'
  headline: string
  bio: string[]
  originStory: string
  journeys: CastProfileJourney[]
  sources: CastProfileSource[]
}

// Original Journey90 editorial copy, fact-checked against the linked official
// TLC cast and journey pages. The jokes stay affectionate; relationship status
// is intentionally not inferred from social media.
const coreCastProfiles: CastProfile[] = [
  {
    personId: 'p-jasmine-pineda',
    headline: 'Panama-to-Michigan main-character energy, with a pageant sash and several hats in the supporting cast.',
    bio: [
      'Jasmine joined the franchise as Gino’s long-distance girlfriend from Panama, and the volume knob has rarely dipped below “event television” since. Their story moved from first-meeting nerves to an engagement, a K-1 move, marriage, therapy and a very complicated attempt at an open relationship.',
      'She is quick, expressive and incapable of pretending a disagreement is fine when it is very much not fine. That candor made her a fixture across Before the 90 Days, the flagship series, Happily Ever After and The Last Resort. Their divorce was confirmed in June 2026 by representatives for both Jasmine and Gino; the relationship that brought her onto the show is now a closed chapter.',
    ],
    originStory: 'After eight months of online dating, Jasmine and Gino came on Before the 90 Days so he could travel from Michigan to Panama, meet her in person and find out whether their internet spark could survive real life—and access to each other’s phones.',
    journeys: [
      {
        label: 'The original journey: Gino & Jasmine',
        partnerName: 'Gino Palazzolo',
        relationshipId: 'ARC-B09532768188',
        era: 'Before the 90 Days S5 → The Last Resort S2 / Happily Ever After S9',
        summary: 'An online romance became a proposal, a move to Michigan and a wedding before trust and intimacy problems pushed the couple into retreat therapy and an open-marriage experiment.',
      },
      {
        label: 'The later chapter: Matt',
        partnerName: 'Matt Branis',
        era: 'The Last Resort S2 and after',
        summary: 'Matt entered the televised story during the open-marriage chapter. TLC later reported that Jasmine and Gino parted ways and that Jasmine welcomed a daughter with Matt.',
      },
    ],
    sources: [
      { label: 'TLC: Gino & Jasmine’s journey', url: 'https://www.tlc.com/shows/90-day-fiance/photo-galleries/gino-jasmine-90-day-fiance-journey-in-photos' },
      { label: 'Us Weekly: Jasmine’s representatives confirm the finalized divorce, June 2026', url: 'https://www.usmagazine.com/celebrity-news/news/90-day-fiances-jasmines-lawyer-breaks-silence-on-divorce/' },
    ],
  },
  {
    personId: 'p-yara-zaya',
    headline: 'Stylish, sharp and never shy about reminding New Orleans that Europe also has apartments.',
    bio: [
      'Yara is a Ukrainian cast member whose relationship with Jovi began through a travel app and quickly turned into a genuinely international romance. She moved to New Orleans on a K-1 visa, adjusted to marriage and motherhood, and repeatedly made the case that “fun” and “Jovi’s idea of fun” are not always the same category.',
      'Across the franchise, Yara’s story has balanced homesickness, family life, war in Ukraine and a long-running debate over where the family should live. She usually arrives with a clear opinion, excellent luggage and no interest in lowering either standard.',
    ],
    originStory: 'Jovi downloaded a travel app because his job took him around the world. He met Yara there, the two met in Budapest, and 90 Day Fiancé followed Yara’s move from Kyiv to New Orleans to marry him.',
    journeys: [
      {
        label: 'The journey: Jovi & Yara',
        partnerName: 'Jovi Dufren',
        relationshipId: 'ARC-55676CA1337A',
        era: '90 Day Fiancé S8 → Happily Ever After S9',
        summary: 'A travel-app match became a K-1 move, a Las Vegas wedding and parenthood, followed by recurring negotiations over nightlife, work travel, Europe and whether their family should grow again.',
      },
    ],
    sources: [
      { label: 'TLC: Jovi & Yara’s journey', url: 'https://www.tlc.com/shows/90-day-fiance/photo-galleries/jovi-yara-90-day-fiance-journey-in-photos' },
    ],
  },
  {
    personId: 'p-paola-mayfield',
    headline: 'One of the franchise originals—and still capable of making Oklahoma and Miami argue over custody of the storyline.',
    bio: [
      'Paola, often called Pao, arrived in the very first season of 90 Day Fiancé after moving from Colombia to build a life with Russ. Her ambitions in modeling, fitness and professional wrestling repeatedly pulled the couple toward bigger cities and bigger decisions.',
      'Pao’s franchise history is practically a family photo album: marriage, career changes, a move to Miami, parenthood and years of Happily Ever After updates. In 2026, she and Russ returned for The Last Resort with nearly twelve years of history to unpack—which is a lot of emotional luggage even before the airline weighs it.',
    ],
    originStory: 'Pao came to the United States on a K-1 visa to marry Russ after the pair met while he was working in Colombia. Season 1 followed the move, the wedding countdown and the first of many conversations about where their shared future should be based.',
    journeys: [
      {
        label: 'The journey: Russ & Paola',
        partnerName: 'Russ Mayfield',
        relationshipId: 'ARC-6C7FAE6C41EF',
        era: '90 Day Fiancé S1 → The Last Resort S3',
        summary: 'Their original K-1 story grew into marriage, a move to Miami, parenthood and a long-running tug-of-war between career opportunities, geography and staying connected as a couple.',
      },
    ],
    sources: [
      { label: 'TLC: Russ & Paola from then to now', url: 'https://www.tlc.com/shows/90-day-fiance/photo-galleries/from-then-to-now-russ-and-paola' },
      { label: 'TLC: The Last Resort season 3 cast', url: 'https://www.tlc.com/shows/90-day-the-last-resort/photo-galleries/season-3-couples-bios' },
    ],
  },
  {
    personId: 'p-rebecca-parrott',
    headline: 'A Georgia romantic with a plane ticket, a Tunisia itinerary and selfies that became franchise folklore.',
    bio: [
      'Rebecca entered Before the 90 Days ready to find out whether her online relationship with Zied could become a real engagement. She traveled to Tunisia, introduced viewers to one of the franchise’s most quoted couples and eventually brought the story home to Georgia for the K-1 chapter.',
      'Her path has included family skepticism, cultural differences, wedding-clock pressure and enough “so much” affection to become its own dialect. Rebecca and Zied later returned for The Last Resort, proving that an established couple can still find fresh ways to make a group-therapy retreat extremely busy.',
    ],
    originStory: 'Rebecca met Zied online and traveled to Tunisia hoping to confirm the relationship in person—and ideally fly home with a ring and a fiancé rather than only a very eventful camera roll.',
    journeys: [
      {
        label: 'The journey: Rebecca & Zied',
        partnerName: 'Zied Hakimi',
        relationshipId: 'ARC-3A90E6C05154',
        era: 'Before the 90 Days S3 → The Last Resort S3',
        summary: 'An online match led to a Tunisia visit, an engagement, a K-1 move and a wedding in the United States before the couple brought years of cultural and communication friction to the retreat.',
      },
    ],
    sources: [
      { label: 'TLC: Rebecca & Zied’s journey', url: 'https://www.tlc.com/shows/90-day-fiance/photo-galleries/rebecca-and-zied--our-journey-so-far' },
      { label: 'TLC: Before the 90 Days season 3 cast', url: 'https://www.tlc.com/shows/90-day-fiance/photo-galleries/meet-the-americans-from-90-day-fiance--before-the-90-days' },
    ],
  },
  {
    personId: 'p-sophie-sierra',
    headline: 'A London-to-Los-Angeles leap that discovered love is complicated and indoor plumbing is apparently negotiable.',
    bio: [
      'Sophie moved from the United Kingdom to Los Angeles to marry Rob after their social-media romance. The glamorous Hollywood life she imagined met a much more budget-conscious reality, while trust issues and mismatched expectations kept their relationship permanently one notification away from another difficult conversation.',
      'After 90 Day Fiancé, Happily Ever After and The Last Resort, Sophie filed for divorce and returned on The Single Life. A girls’ trip to the Dominican Republic produced an unexpected new franchise crossover: Pedro.',
    ],
    originStory: 'Sophie and Rob met on social media and joined 90 Day Fiancé when she moved from the UK to Los Angeles on a K-1 visa. The mission was marriage; the surprise subplot was discovering exactly which household amenities Rob’s apartment did not include.',
    journeys: [
      {
        label: 'The original journey: Rob & Sophie',
        partnerName: 'Rob Warne',
        relationshipId: 'ARC-6E9479AA5D80',
        era: '90 Day Fiancé S10 → The Last Resort S2',
        summary: 'Their online relationship became a K-1 move and marriage, but recurring trust, money and communication problems followed them through Happily Ever After and into retreat therapy.',
      },
      {
        label: 'The later chapter: Sophie & Pedro',
        partnerName: 'Pedro Jimeno',
        relationshipId: 'ARC-06EDA993968C',
        era: 'The Single Life S5',
        summary: 'While visiting the Dominican Republic with friends, Sophie met fellow franchise alum Pedro. The spark was real; agreeing on what that spark meant took considerably longer.',
      },
    ],
    sources: [
      { label: 'TLC: 90 Day Fiancé season 10 cast', url: 'https://www.tlc.com/shows/90-day-fiance/photo-galleries/90-day-fiance-new-couples-season-10' },
      { label: 'TLC: The Single Life season 5 cast', url: 'https://www.tlc.com/shows/90-day-the-single-life/photo-galleries/season-5-90-day-singles-bios-updates' },
    ],
  },
  {
    personId: 'p-chantel-everett',
    headline: 'A Spanish lesson became a marriage, a family spinoff and one of reality TV’s longest-running group chats.',
    bio: [
      'Chantel first appeared as an Atlanta nursing student who had fallen for Pedro in the Dominican Republic. She initially told her family he was visiting on a student visa rather than arriving as her fiancé—a plan with the shelf life of an unrefrigerated empanada.',
      'The relationship grew into multiple Happily Ever After seasons and The Family Chantel before ending in divorce. Chantel later re-entered dating on The Single Life, connected with Greek soccer player Giannis and joined the first Hunt for Love retreat, where a friendship with Ashley Bowen eventually became something more.',
    ],
    originStory: 'A friend connected Chantel with Pedro so she could practice Spanish. Attraction quickly replaced vocabulary homework, Pedro proposed on her third visit, and 90 Day Fiancé followed his K-1 arrival and their secret engagement.',
    journeys: [
      {
        label: 'The original journey: Chantel & Pedro',
        partnerName: 'Pedro Jimeno',
        relationshipId: 'ARC-35AAB4B45C12',
        era: '90 Day Fiancé S4 → Happily Ever After S4 / The Family Chantel',
        summary: 'A language connection became a K-1 marriage and a family-centered spinoff before the couple separated and divorced.',
      },
      {
        label: 'The later chapter: Chantel & Giannis',
        partnerName: 'Giannis Varouxakis',
        relationshipId: 'ARC-1B2F99EAC8E9',
        era: 'The Single Life S4',
        summary: 'On a post-divorce trip to Greece, Chantel met soccer player Giannis and explored whether a vacation spark could survive the trip home.',
      },
      {
        label: 'The newest chapter: Chantel & Ashley',
        partnerName: 'Ashley Bowen',
        era: 'Hunt for Love S1 → 90 Day Diaries S8',
        summary: 'A friendship at the Tulum retreat grew into a relationship after filming. Their Diaries story later reached a proposal—this time with the engagement very much not hidden from the family.',
      },
    ],
    sources: [
      { label: 'TLC: Chantel’s journey', url: 'https://www.tlc.com/shows/90-day-the-single-life/photo-galleries/chantel-90-day-fiance-journey-so-far-photos' },
      { label: 'TLC: Hunt for Love singles', url: 'https://www.tlc.com/shows/90-day-hunt-for-love/90-day-hunt-for-love-meet-the-90-day-singles' },
      { label: 'TLC: Chantel & Ashley’s engagement', url: 'https://www.tlc.com/shows/90-day-diaries/photo-galleries/90-day-fiance-chantel-ashley-proposal-engagement-photos' },
    ],
  },
  {
    personId: 'p-thais-ramone',
    headline: 'Patrick went looking for a Portuguese teacher and found a fiancée; John found permanent front-row seating.',
    bio: [
      'Thaís met Patrick after he opened a dating app while trying to improve his Portuguese in Brazil. Several trips, plenty of gifts and one proposal later, she moved to Texas on a K-1 visa—without having fully sold her father on the plan.',
      'Her story with Patrick has covered marriage, parenthood, visits back to Brazil and the ever-present gravitational pull of Patrick’s brother John. They later brought their marriage to The Last Resort, because apparently a castle is as good a place as any for a serious relationship summit.',
    ],
    originStory: 'Patrick was visiting Brazil and needed help with Portuguese. Thaís swiped right, the language practice became a relationship, and Season 9 followed her move to the United States to marry him.',
    journeys: [
      {
        label: 'The journey: Patrick & Thaís',
        partnerName: 'Patrick Mendes',
        relationshipId: 'ARC-B15A1B3EFFC7',
        era: '90 Day Fiancé S9 → The Last Resort S3',
        summary: 'A dating-app match grew through repeated Brazil visits, a K-1 move, marriage and parenthood before financial, family and communication pressures brought them to the retreat.',
      },
    ],
    sources: [
      { label: 'TLC: 90 Day Fiancé season 9 cast', url: 'https://www.tlc.com/shows/90-day-fiance/photo-galleries/meet-the-new-90-day-fiance-season-9-couples' },
      { label: 'TLC: The Last Resort season 3 cast', url: 'https://www.tlc.com/shows/90-day-the-last-resort/photo-galleries/season-3-couples-bios' },
    ],
  },
  {
    personId: 'p-ariela-weinberg',
    headline: 'A world trip became an international family plan before anyone had time to update the itinerary.',
    bio: [
      'Ariela, usually called Ari, met Biniyam while traveling in Ethiopia after her divorce. Three months later she was pregnant, and The Other Way followed her decision to return to Ethiopia for the birth of their son and try to build a life together.',
      'Their story moved from Addis Ababa to New Jersey, with family expectations, religion, money, MMA training and trust all requesting speaking roles. By The Last Resort, they arrived separately but still agreed to see whether the relationship could be repaired.',
    ],
    originStory: 'Ari met Bini while waiting for a taxi in Ethiopia. Their whirlwind relationship and unexpected pregnancy turned a travel chapter into a move abroad, a new baby and a very full Season 2 storyline.',
    journeys: [
      {
        label: 'The journey: Ariela & Biniyam',
        partnerName: 'Biniyam Shibre',
        relationshipId: 'ARC-84F46727A2A5',
        era: 'The Other Way S2 → The Last Resort S2',
        summary: 'A chance meeting in Ethiopia became parenthood, an engagement, life in two countries and a move to the United States, followed by a retreat attempt to work through distance and trust.',
      },
    ],
    sources: [
      { label: 'TLC: Ariela & Biniyam’s journey', url: 'https://www.tlc.com/shows/90-day-fiance/photo-galleries/ariela-biniyam-90-day-fiance-journey-so-far-in-photos' },
    ],
  },
  {
    personId: 'p-elizabeth-potthast',
    headline: 'She met Andrei in Dublin and brought him to Florida, where the family meeting agenda has never once been short.',
    bio: [
      'Elizabeth, also known as Libby, connected with Andrei on a dating app and met him while traveling in Dublin, where he was living after leaving Moldova. Their engagement led to a K-1 move, a Florida wedding and a franchise storyline in which Elizabeth’s relatives are rarely more than one opinion away.',
      'Across Happily Ever After, the couple built a family, held a second wedding in Moldova and mixed marriage with real-estate ambitions and extended-family friction. Their story has endured long enough for even a casual dinner to arrive with multiple seasons of backstory.',
    ],
    originStory: 'Elizabeth met Andrei in Dublin during a solo trip after connecting with him on a dating app. 90 Day Fiancé followed his move to Florida, their wedding countdown and her family’s extremely active review process.',
    journeys: [
      {
        label: 'The journey: Elizabeth & Andrei',
        partnerName: 'Andrei Castravet',
        relationshipId: 'ARC-7A6D723EF5C6',
        era: '90 Day Fiancé S5 → Happily Ever After S9',
        summary: 'A Dublin meeting became a K-1 marriage, two weddings, two children and a long-running negotiation over money, business, Moldova and the boundaries of the Potthast family orbit.',
      },
    ],
    sources: [
      { label: 'TLC: Elizabeth & Andrei’s love story', url: 'https://www.tlc.com/shows/90-day-fiance/photo-galleries/elizabeth-and-andrei-our-journey-in-photos' },
    ],
  },
  {
    personId: 'p-kara-bass',
    headline: 'A work trip produced a fiancé, a K-1 story and enough plot turns to keep the balloon arch fully inflated.',
    bio: [
      'Kara met Guillermo, a Venezuelan living in the Dominican Republic, while she was there for work. A later job relocation put them under the same roof, they became engaged, and the pandemic eventually separated them until Guillermo’s move to Virginia for Season 9.',
      'Their franchise life expanded through marriage, parenthood, Happily Ever After and The Last Resort. Kara is now officially returning as a single for Hunt for Love Season 2, where the relationship forecast is once again “bring sunscreen and expect variables.”',
    ],
    originStory: 'Kara and Guillermo kept in touch after meeting during her Dominican Republic work trip, then lived together when her job brought her back. Season 9 followed their reunion in Virginia and their 90-day race to the altar.',
    journeys: [
      {
        label: 'The original journey: Kara & Guillermo',
        partnerName: 'Guillermo Rojer',
        relationshipId: 'ARC-6FC42EB29A8D',
        era: '90 Day Fiancé S9 → The Last Resort S3',
        summary: 'A work-trip meeting became cohabitation in the Dominican Republic, an engagement, a K-1 move, marriage and parenthood before the couple entered retreat therapy while living separately.',
      },
      {
        label: 'A new single chapter',
        era: 'Hunt for Love S2',
        summary: 'TLC announced Kara as one of seven returning singles heading to Puerto Rico to meet new people. No new canonical partner is attached to this chapter yet.',
      },
    ],
    sources: [
      { label: 'TLC: 90 Day Fiancé season 9 cast', url: 'https://www.tlc.com/shows/90-day-fiance/photo-galleries/meet-the-new-90-day-fiance-season-9-couples' },
      { label: 'TLC: Hunt for Love season 2 cast', url: 'https://www.tlc.com/shows/90-day-hunt-for-love/90-day-hunt-for-love-season-2-singles-bios-photos' },
    ],
  },
  {
    personId: 'p-ed-brown',
    headline: 'A franchise veteran whose relationship timeline has more reversals than some prestige dramas.',
    bio: [
      'San Diego photographer Ed became “Big Ed” to viewers when he traveled to the Philippines to meet Rose, his much younger online girlfriend, on Before the 90 Days. Their trip ended without an engagement, but Ed’s franchise passport was only getting started.',
      'On The Single Life he asked out Liz, a manager at a restaurant he frequented. That relationship became a cycle of dating, breakups, reconciliation and engagement across several shows. If emotional whiplash earned airline miles, this profile would have lounge access.',
    ],
    originStory: 'Ed first came on the show to meet Rose in the Philippines and decide whether their online relationship could become a proposal and a shared future in the United States.',
    journeys: [
      {
        label: 'The original journey: Big Ed & Rose',
        partnerName: 'Rosemarie Vega',
        relationshipId: 'ARC-7966A39D3E56',
        era: 'Before the 90 Days S4',
        summary: 'An online relationship brought Ed to the Philippines to meet Rose and her son. Differences over trust, respect and future children ended the relationship before an engagement.',
      },
      {
        label: 'The later journey: Big Ed & Liz',
        partnerName: 'Liz Woods',
        relationshipId: 'ARC-257C9F77A6EF',
        era: 'The Single Life S1 → Happily Ever After S8',
        summary: 'Ed met Liz at a San Diego restaurant and asked her out on The Single Life. Their story moved through repeated breakups, an engagement, Happily Ever After and The Last Resort before ending ahead of their planned wedding.',
      },
    ],
    sources: [
      { label: 'TLC: Big Ed’s journey', url: 'https://www.tlc.com/inside-tlc/Big-Ed-journey-so-far-90-day-single-life' },
    ],
  },
  {
    personId: 'p-michael-ilesanmi',
    headline: 'A social-media hello became a transatlantic marriage and a visa storyline long enough to need its own passport.',
    bio: [
      'Michael entered the franchise from Nigeria after messaging Angela online. Their long-distance relationship stretched through visits, proposals, a Nigerian wedding and years of immigration delays before he eventually moved to the United States.',
      'The story was marked by intense arguments, cultural differences and recurring trust problems. Now divorced, Michael is officially returning on Hunt for Love Season 2 to look for romance—and, according to TLC, a best friend—which sounds refreshingly calm by franchise standards.',
    ],
    originStory: 'Michael messaged Angela on social media, they built a relationship online and Before the 90 Days followed her first trip from Georgia to Nigeria to meet him face to face.',
    journeys: [
      {
        label: 'The original journey: Angela & Michael',
        partnerName: 'Angela Deem',
        relationshipId: 'ARC-51D2491498E4',
        era: 'Before the 90 Days S2 → Happily Ever After S8',
        summary: 'A surprise social-media message became a long-distance relationship, an engagement, marriage in Nigeria, years of visa delays and eventually a move to the United States.',
      },
      {
        label: 'A new single chapter',
        era: 'Hunt for Love S2',
        summary: 'TLC announced Michael as a newly divorced returning single looking for both romance and a genuine best friend in Puerto Rico. The new season has not established a canonical partner yet.',
      },
    ],
    sources: [
      { label: 'TLC: Angela & Michael’s journey', url: 'https://www.tlc.com/shows/90-day-fiance/photo-galleries/angela-michael-90-day-fiance-journey-in-photos' },
      { label: 'TLC: Hunt for Love season 2 cast', url: 'https://www.tlc.com/shows/90-day-hunt-for-love/90-day-hunt-for-love-season-2-singles-bios-photos' },
    ],
  },
  {
    personId: 'p-guillermo-rojer',
    headline: 'Venezuelan patience, Virginia practicality and the rare ability to lower the room’s blood pressure without leaving it.',
    bio: [
      'Guillermo entered the franchise as Kara’s younger fiancé from Venezuela, whom she met while both were living in the Dominican Republic. After the pandemic turned their engagement into a long-distance waiting game, he moved to Virginia on a K-1 visa and discovered that the 90-day clock comes with wedding budgets, family opinions and approximately zero quiet group chats.',
      'Often the calmest person in a scene that had scheduled no calm, Guillermo built a home with Kara and became a father before their marriage began to fracture. Happily Ever After and The Last Resort followed the harder chapter: separate homes, lingering feelings and the question of whether love alone can complete the repair list.',
    ],
    originStory: 'Kara met Guillermo while working in the Dominican Republic, then later moved in with him when her job brought her back. They got engaged quickly, spent nine months apart during the pandemic and joined Season 9 when Guillermo’s K-1 visa finally brought him to Charlottesville.',
    journeys: [
      {
        label: 'The journey: Kara & Guillermo',
        partnerName: 'Kara Bass',
        relationshipId: 'ARC-6FC42EB29A8D',
        era: '90 Day Fiancé S9 → The Last Resort S3',
        summary: 'A work-trip meeting became cohabitation in the Dominican Republic, an engagement, a K-1 move, marriage and parenthood before the couple entered retreat therapy while living separately.',
      },
    ],
    sources: [
      { label: 'TLC: 90 Day Fiancé season 9 cast', url: 'https://www.tlc.com/shows/90-day-fiance/photo-galleries/meet-the-new-90-day-fiance-season-9-couples' },
      { label: 'TLC: Happily Ever After? season 9 first look', url: 'https://www.tlc.com/shows/90-day-fiance-happily-ever-after/articles/90-day-fiance-happily-ever-after-season-9-couples-first-look' },
      { label: 'TLC: The Last Resort season 3 cast', url: 'https://www.tlc.com/shows/90-day-the-last-resort/photo-galleries/season-3-couples-bios' },
    ],
  },
  {
    personId: 'p-gino-palazzolo',
    headline: 'An automotive engineer with a formidable hat collection and a relationship timeline that needed its own carry-on.',
    bio: [
      'Gino arrived on Before the 90 Days as a Michigan automotive engineer traveling to Panama to meet Jasmine after eight months online. What followed was a proposal, a prenup battle, a K-1 move, marriage and enough trust crises to keep several reunion couches fully employed.',
      'His soft-spoken delivery and famously loyal hats often made him look like the quiet half of the pairing, but Gino has never lacked plot. After couples therapy, an open-marriage experiment and a breakup, he returned on The Single Life to try dating again—because apparently one franchise roller coaster was merely the warm-up. His divorce from Jasmine was confirmed in June 2026; the season’s unfinished-paperwork storyline belongs to its filming chapter, not their current legal label.',
    ],
    originStory: 'After a divorce, Gino met Jasmine on an international dating site and fell for the Panamanian teacher’s beauty, brains and big personality. Before the 90 Days followed his first trip to Panama, where he hoped to turn eight months of video calls into an in-person future—and perhaps a proposal.',
    journeys: [
      {
        label: 'The original journey: Gino & Jasmine',
        partnerName: 'Jasmine Pineda',
        relationshipId: 'ARC-B09532768188',
        era: 'Before the 90 Days S5 → Happily Ever After S9',
        summary: 'An online romance became a proposal, a move to Michigan and a wedding before trust and intimacy problems pushed the couple into retreat therapy and an open-marriage experiment.',
      },
      {
        label: 'A new single chapter',
        partnerName: 'Natalie Mordovtseva',
        era: 'The Single Life S5',
        summary: 'Newly separated, Gino tried dating apps and reconnected with former flame Natalie while his unfinished divorce made a simple fresh start considerably less simple.',
      },
    ],
    sources: [
      { label: 'TLC: Gino & Jasmine’s journey', url: 'https://www.tlc.com/shows/90-day-fiance/photo-galleries/gino-jasmine-90-day-fiance-journey-in-photos' },
      { label: 'TLC: The Single Life season 5 cast', url: 'https://www.tlc.com/shows/90-day-the-single-life/photo-galleries/season-5-90-day-singles-bios-updates' },
      { label: 'TLC: The Single Life season 5 recap', url: 'https://www.tlc.com/shows/90-day-the-single-life/photo-galleries/90-day-the-single-life-season-5-drama-episodes-recap' },
      { label: 'Us Weekly: Gino’s representative confirms the finalized divorce, June 2026', url: 'https://www.usmagazine.com/celebrity-news/news/90-day-fiances-gino-palazzolo-finalizes-jasmine-divorce/' },
    ],
  },
  {
    personId: 'p-jovi-dufren',
    headline: 'A travel-app romantic whose work passport, New Orleans nightlife and family calendar are always negotiating gate changes.',
    bio: [
      'Jovi’s job took him around the world, so he downloaded a travel app and met Yara—an efficient decision that somehow produced Budapest dates, a K-1 move, a Las Vegas wedding and daughter Mylah. Back in New Orleans, his long work trips and enthusiasm for nightlife became recurring characters with suspiciously strong contracts.',
      'Across Happily Ever After and The Last Resort, Jovi and Yara have debated Europe, Miami, another baby and what “a quiet night out” is legally allowed to mean. He brings restless traveler energy to family life, but when the stakes rise, the story keeps returning to the same destination: he wants his family together.',
    ],
    originStory: 'Jovi used a travel app to meet people during his globe-spanning work trips and connected with Yara from Ukraine. They met in Budapest, built a long-distance relationship and joined Season 8 when Yara moved from Kyiv to New Orleans on a K-1 visa.',
    journeys: [
      {
        label: 'The journey: Jovi & Yara',
        partnerName: 'Yara Zaya',
        relationshipId: 'ARC-55676CA1337A',
        era: '90 Day Fiancé S8 → Happily Ever After S9',
        summary: 'A travel-app match became a K-1 move, a Las Vegas wedding and parenthood, followed by recurring negotiations over nightlife, work travel, Europe and whether their family should grow again.',
      },
    ],
    sources: [
      { label: 'TLC: Jovi & Yara’s journey', url: 'https://www.tlc.com/shows/90-day-fiance/photo-galleries/jovi-yara-90-day-fiance-journey-in-photos' },
      { label: 'TLC: The Last Resort season 1 cast', url: 'https://www.tlc.com/shows/90-day-the-last-resort/photo-galleries/90-Day-Fiance-The-Last-Resort-Couples-Bios' },
    ],
  },
  {
    personId: 'p-russ-mayfield',
    headline: 'An engineer, franchise original and veteran of the longest-running Oklahoma-to-Miami commute in reality television.',
    bio: [
      'Russ met Paola while working in Colombia, then brought their relationship home to Oklahoma for the very first season of 90 Day Fiancé. Their marriage became an early franchise blueprint: culture shock, skeptical relatives, career negotiations and a recurring debate over whether Miami was a destination or a full-time cast member.',
      'Over the years, Russ supported Pao through modeling, fitness, wrestling, grief and parenthood while occasionally looking like a man calculating the structural load of every conversation. The Last Resort brought them back after nearly twelve years, with separate ambitions, old hurt and a marriage that needed more than an oil change.',
    ],
    originStory: 'Russ met Pao while his engineering work took him to Colombia. She came to the United States on a K-1 visa, and Season 1 followed their effort to turn an international romance into a marriage while Pao adjusted to Russ’s conservative Oklahoma hometown.',
    journeys: [
      {
        label: 'The journey: Russ & Paola',
        partnerName: 'Paola Mayfield',
        relationshipId: 'ARC-6C7FAE6C41EF',
        era: '90 Day Fiancé S1 → The Last Resort S3',
        summary: 'Their original K-1 story grew into marriage, a move to Miami, parenthood and a long-running tug-of-war between career opportunities, geography and staying connected as a couple.',
      },
    ],
    sources: [
      { label: 'TLC: Russ & Paola from then to now', url: 'https://www.tlc.com/shows/90-day-fiance/photo-galleries/from-then-to-now-russ-and-paola' },
      { label: 'TLC: The Last Resort season 3 cast', url: 'https://www.tlc.com/shows/90-day-the-last-resort/photo-galleries/season-3-couples-bios' },
    ],
  },
  {
    personId: 'p-andrei-castravet',
    headline: 'Moldovan directness, Florida real estate and a family-meeting calendar that has never once known peace.',
    bio: [
      'Andrei met Elizabeth while living in Dublin, then moved to Florida on a K-1 visa and walked directly into one of the franchise’s busiest extended families. They married during Season 5, later held a second wedding in Moldova and built a family while every career decision attracted a committee review.',
      'Across Happily Ever After, Andrei moved from job debates to the family real-estate business, immigration questions and friendships with fellow franchise couples. His style is famously direct—subtle enough to require its own subtitles, even when everyone is already speaking English.',
    ],
    originStory: 'Andrei and Elizabeth connected on a dating app and met while she was traveling in Dublin, where he was living after leaving Moldova. 90 Day Fiancé followed his move to Florida, their 90-day wedding countdown and his introduction to a family that believes every decision deserves a roundtable.',
    journeys: [
      {
        label: 'The journey: Elizabeth & Andrei',
        partnerName: 'Elizabeth Potthast',
        relationshipId: 'ARC-7A6D723EF5C6',
        era: '90 Day Fiancé S5 → Happily Ever After S9',
        summary: 'A Dublin meeting became a K-1 marriage, two weddings, two children and a long-running negotiation over money, business, Moldova and the boundaries of the Potthast family orbit.',
      },
    ],
    sources: [
      { label: 'TLC: Elizabeth & Andrei’s love story', url: 'https://www.tlc.com/shows/90-day-fiance/photo-galleries/elizabeth-and-andrei-our-journey-in-photos' },
      { label: 'TLC: Happily Ever After? season 9 first look', url: 'https://www.tlc.com/shows/90-day-fiance-happily-ever-after/articles/90-day-fiance-happily-ever-after-season-9-couples-first-look' },
    ],
  },
  {
    personId: 'p-angela-deem',
    headline: 'A Georgia grandmother whose one online message became an eight-season transatlantic saga and a mountain of visa paperwork.',
    bio: [
      'Angela entered Before the 90 Days after Michael messaged her online, prompting a first trip from Georgia to Nigeria and an engagement that immediately came with opinions from two continents. Their story continued through a K-1 denial, a Nigerian wedding and years of long-distance marriage while the spousal-visa process took the scenic route.',
      'Across Happily Ever After and The Last Resort, trust, family expectations and immigration pressure kept the relationship in near-constant negotiation. Michael eventually reached the United States, but the reunion was brief; TLC now describes their marriage as ended, closing one of the franchise’s longest chapters without pretending the paperwork was ever the only complication.',
    ],
    originStory: 'Michael sent Angela a social-media message, and the two built a long-distance relationship before she flew to Nigeria to meet him. Before the 90 Days followed that first visit, where Angela hoped to decide whether the online connection could become an engagement and a shared life.',
    journeys: [
      {
        label: 'The journey: Angela & Michael',
        partnerName: 'Michael Ilesanmi',
        relationshipId: 'ARC-51D2491498E4',
        era: 'Before the 90 Days S2 → Happily Ever After S8',
        summary: 'A surprise social-media message became a long-distance relationship, an engagement, marriage in Nigeria, years of visa delays and finally a short-lived reunion in the United States.',
      },
    ],
    sources: [
      { label: 'TLC: Angela & Michael’s journey', url: 'https://www.tlc.com/shows/90-day-fiance/photo-galleries/angela-michael-90-day-fiance-journey-in-photos' },
      { label: 'TLC: The Last Resort season 1 cast', url: 'https://www.tlc.com/shows/90-day-the-last-resort/photo-galleries/90-Day-Fiance-The-Last-Resort-Couples-Bios' },
    ],
  },
  {
    personId: 'p-biniyam-shibre',
    headline: 'A dancer, musician, acrobat and MMA fighter whose career list may be the franchise’s most ambitious opening credits.',
    bio: [
      'Biniyam met Ariela while she was traveling in Ethiopia, and their whirlwind romance became a family story when she learned she was pregnant. The Other Way followed Ari’s return to Addis Ababa for their son’s birth while Bini balanced fatherhood with enough creative and athletic jobs to make a normal résumé ask for intermission.',
      'Their story later moved to New Jersey, where Bini pursued an MMA career while the couple worked through money, family and trust. By The Last Resort, they arrived separately and used therapy to ask a difficult but practical question: whether repairing the marriage or reshaping the co-parenting relationship was the healthier next step.',
    ],
    originStory: 'Ariela met Biniyam while waiting for a taxi in Ethiopia. Three months later she was pregnant, and The Other Way followed her decision to return, give birth near Bini and find out whether a travel romance could become a shared home.',
    journeys: [
      {
        label: 'The journey: Ariela & Biniyam',
        partnerName: 'Ariela Weinberg',
        relationshipId: 'ARC-84F46727A2A5',
        era: 'The Other Way S2 → The Last Resort S2',
        summary: 'A chance meeting in Ethiopia became parenthood, an engagement, life in two countries and a move to the United States, followed by a retreat attempt to work through distance and trust.',
      },
    ],
    sources: [
      { label: 'TLC: Ariela & Biniyam’s journey', url: 'https://www.tlc.com/shows/90-day-fiance/photo-galleries/ariela-biniyam-90-day-fiance-journey-so-far-in-photos' },
      { label: 'TLC: The Last Resort season 2 cast', url: 'https://www.tlc.com/shows/90-day-the-last-resort/photo-galleries/90-day-the-last-resort-meet-the-new-couples-bios' },
    ],
  },
  {
    personId: 'p-rob-warne',
    headline: 'A Los Angeles romantic whose franchise journey upgraded the apartment, changed countries and kept the trust issues fully furnished.',
    bio: [
      'Rob met Sophie on social media and welcomed her from London for a K-1 chapter in Los Angeles. Their Season 10 story made money, online flirting and one famously outdoor bathroom part of the wedding countdown; they married, but the same trust problems followed them into Happily Ever After and retreat therapy.',
      'After the relationship ended, Rob joined Hunt for Love in Tulum and discovered that being single on a tropical retreat is not the same as keeping things simple. An early connection with Elise shifted toward Jeniffer, turning his fresh start into a love triangle before anyone had finished applying sunscreen.',
    ],
    originStory: 'Rob and Sophie met on social media and built a long-distance relationship before she moved from the United Kingdom to Los Angeles on a K-1 visa. 90 Day Fiancé followed their attempt to turn online chemistry into a marriage on a very real-world budget.',
    journeys: [
      {
        label: 'The original journey: Rob & Sophie',
        partnerName: 'Sophie Sierra',
        relationshipId: 'ARC-6E9479AA5D80',
        era: '90 Day Fiancé S10 → The Last Resort S2',
        summary: 'Their online relationship became a K-1 move and marriage, but recurring trust, money and communication problems followed them through Happily Ever After and into retreat therapy.',
      },
      {
        label: 'The retreat connection: Rob & Elise',
        partnerName: 'Elise Benson',
        relationshipId: 'ARC-6CB6773F8F74',
        era: 'Hunt for Love S1',
        summary: 'Rob and Elise formed an early connection in Tulum before his attention shifted and the retreat’s dating map became considerably more crowded.',
      },
      {
        label: 'The later retreat chapter: Jeniffer & Rob',
        partnerName: 'Jeniffer Tarazona',
        relationshipId: 'ARC-4A522D4B0E29',
        era: 'Hunt for Love S1',
        summary: 'Rob and Jeniffer explored a stronger connection while her history with Cole complicated the picture. The season presented a possibility, not a permanent-status announcement.',
      },
    ],
    sources: [
      { label: 'TLC: 90 Day Fiancé season 10 cast', url: 'https://www.tlc.com/shows/90-day-fiance/photo-galleries/90-day-fiance-new-couples-season-10' },
      { label: 'TLC: The Last Resort season 2 cast', url: 'https://www.tlc.com/shows/90-day-the-last-resort/photo-galleries/90-day-the-last-resort-meet-the-new-couples-bios' },
      { label: 'TLC: Hunt for Love singles', url: 'https://www.tlc.com/shows/90-day-hunt-for-love/90-day-hunt-for-love-meet-the-90-day-singles' },
      { label: 'TLC: Hunt for Love season recap', url: 'https://www.tlc.com/shows/90-day-hunt-for-love/90-day-hunt-for-love-drama-recap' },
    ],
  },
]

export const castProfiles: CastProfile[] = [...coreCastProfiles, ...batch50Profiles, ...nextBatch50Profiles, ...researchedProfilesB, ...researchedProfilesA, ...researchedProfilesC, ...legacyProfilesB, ...legacyProfilesA, ...legacyProfilesC, ...researchedProfilesCFollowup, ...finalCarmellaProfiles, ...finalIdentityProfiles, ...finalHuntProfilesCE, ...finalHuntProfilesCT, ...familyChantelCanonProfiles, ...darceyStaceyCanonProfiles]

refreshExistingCanonProfiles(castProfiles)

export function profileForPerson(personId: string) {
  return castProfiles.find((profile) => profile.personId === personId)
}
